/**
 * Balance tunables — numbers only, no logic (ADR-006). v0.2 values are
 * PLACEHOLDERS (v0.8 owns balance). Spec: docs/v0.2.0_RFC_Specification.md.
 */

/** Tick model (v0.1 RFC §3, ruled — unchanged). */
export const TICKS = {
  onlineMs: 1_000,
  offlineMs: 2_000,
  offlineCapMs: 30 * 60 * 1_000, // v0.13.2: 8h → 30min — caps the offline runaway (Russell; the online criminal rate still needs a rebalance)
} as const;

/** LOGIC version — the internal rules axis (CONVENTIONS §1.2). Bumped on
 * behavior changes that don't alter the save shape. */
export const LOGIC = { version: 13 } as const; // 13 = v0.15 criminal decouple + the Fixer (12 = event cards, 11 = offline cap, 10 = scheduler, 9 = ×4 pacing)

export const SAVE = {
  key: "halfway.save",
  /** SCHEMA 10 — v0.15 adds run.fixerLevel (the DEA-axis mitigation role)
   *  and splits the criminal hybrids into pure earner + pure mitigator;
   *  migrations 1→…→9→10. */
  version: 10,
  autosaveEveryTicks: 10,
} as const;

/** v0.13 "The Texture" — the event scheduler (RFC §3; every number
 * Control-Room-tunable). ONLINE-ONLY mechanism (RULED §8.5): weights
 * are per-LIVE-tick chances once a channel's cooldown lapses; loot
 * lifetimes and cooldowns are remaining-tick counters that freeze
 * offline. Cadence RULED §8.4 "for starters". */
export const EVENTS = {
  /** Loot channel (one per OPEN care tier). */
  loot: {
    weight: 1 / 60, // avg ~60 live seconds to fire once off cooldown
    cooldownTicks: 150, // ~2.5 min between resolutions per tier
    lifetimeTicks: 45, // RULED ~45s to grab it
    /** Money-kind value = this × the tier's promotionPayout. */
    moneyValueMult: 0.5,
    /** Chance a spawn is CONTRABAND (vs money), per tier. */
    contrabandChance: 0.35,
  },
  /** Contraband economics (RULED §8.3b). */
  contraband: {
    heatPerGrab: 4,
    /** Fence multiplier on the money value when the criminal track is open. */
    fenceMult: 2.0,
    /** Multiplier when the track is NOT open (sold quietly, same heat). */
    dudMult: 1.0,
    /** Street Knowledge per grab when the track is open. */
    skTrickle: 1,
  },
  /** Satire channel (global). */
  satire: {
    weight: 1 / 90,
    cooldownTicks: 300, // ~5 min floor between toasts (RULED cadence)
  },
  /** Event-card channel (one per SCENE — RFC v0.14). */
  card: {
    weight: 1 / 120, // avg ~2 min to fire once a scene's channel is off cooldown
    cooldownTicks: 600, // ~10 min between cards per scene
    lifetimeTicks: 50, // ~50s comfortable window (RULED §8.4); expiry = passive
  },
} as const;

/** Office funnel (unchanged from v0.1). */
export const OFFICE = {
  queueCap: 6,
  recruiterIntervalByLevel: [Infinity, 40, 28, 20, 16, 12], // 0.12.3: game-pace ×4 (Russell)
  recruiterBaseCost: 50,
  recruiterCostGrowth: 2.2,
} as const;

/** Care-tier ids in continuum order (alumni is the pool beyond them). */
export const CARE_TIERS = ["detox", "residential", "php", "iop", "soberLiving"] as const;
export type CareTierId = (typeof CARE_TIERS)[number];

export interface TierConfig {
  title: string;
  /** The manual verb (context label for the same tend semantics). */
  manualVerb: string;
  /** One-character-that-upgrades automator (name provisional per ruling). */
  automatorName: string;
  capacity: number;
  /** Ticks to complete at Standard discharge policy. */
  completionTicks: number;
  promotionPayout: number;
  /** M8 base relapse, percentage points. */
  baseRelapse: number;
  issueChancePerTick: number;
  /** Per-tier ISSUE flavor (copy pass v0.9 finalizes wording). */
  issueFlavor: string;
  automatorBaseCost: number;
  /** Signage gag line (satire lives in signage). */
  gag: string;
  /** Palette key for the floor's accent (slot fill). */
  accent: "teal" | "mustard" | "green" | "coralRed" | "gray";
}

export const TIERS: Record<CareTierId, TierConfig> = {
  detox: {
    title: "DETOX",
    manualVerb: "Tend bed",
    automatorName: "Technician",
    capacity: 3,
    completionTicks: 200,
    promotionPayout: 100,
    baseRelapse: 25,
    issueChancePerTick: 0.025,
    issueFlavor: "medical emergency",
    automatorBaseCost: 100,
    gag: "medically supervised*   *supervision sold separately",
    accent: "teal",
  },
  residential: {
    title: "RESIDENTIAL",
    manualVerb: "Counseling session",
    automatorName: "Counselor",
    capacity: 4,
    completionTicks: 360,
    promotionPayout: 250,
    baseRelapse: 20,
    issueChancePerTick: 0.02,
    issueFlavor: "contraband found",
    automatorBaseCost: 250,
    gag: "a home away from home*   *home not included",
    accent: "mustard",
  },
  php: {
    title: "PHP",
    manualVerb: "Run group session",
    automatorName: "Group Therapist",
    capacity: 6,
    completionTicks: 560,
    promotionPayout: 500,
    baseRelapse: 18,
    issueChancePerTick: 0.018,
    issueFlavor: "skipped group",
    automatorBaseCost: 600,
    gag: "partial hospitalization*   *emphasis on partial",
    accent: "green",
  },
  iop: {
    title: "IOP",
    manualVerb: "Check in patient",
    automatorName: "Case Manager",
    capacity: 8,
    completionTicks: 800,
    promotionPayout: 900,
    baseRelapse: 15,
    issueChancePerTick: 0.015,
    issueFlavor: "missed check-in",
    automatorBaseCost: 1400,
    gag: "intensive outpatient*   *intensity may vary",
    accent: "coralRed",
  },
  soberLiving: {
    title: "SOBER LIVING",
    manualVerb: "House check",
    automatorName: "House Manager",
    capacity: 6,
    completionTicks: 1120,
    promotionPayout: 1500,
    baseRelapse: 12,
    issueChancePerTick: 0.012,
    issueFlavor: "house incident",
    automatorBaseCost: 3200,
    gag: "the halfway point*   *rent due weekly",
    accent: "gray",
  },
} as const;

/** Manual-tend progress boost (uniform across tiers for now). */
export const TEND_BOOST = 8;

/** Automator actions-per-tick by level — one table for every care tier. */
export const AUTOMATOR_ACTIONS_BY_LEVEL = [0, 1, 2, 3, 4, 5] as const;
export const AUTOMATOR_COST_GROWTH = 2.2;

/** M6 discharge policy: completion-speed vs relapse trade, one lever. */
export const DISCHARGE = {
  aggressive: { ticksMultiplier: 0.75, relapseDelta: +8 },
  standard: { ticksMultiplier: 1.0, relapseDelta: 0 },
  extended: { ticksMultiplier: 1.35, relapseDelta: -6 },
} as const;
export type DischargePolicy = keyof typeof DISCHARGE;

/** M8 relapse-formula terms (LIVE set; reserved terms arrive with their eras). */
export const RELAPSE = {
  clampMin: 2,
  clampMax: 85,
  overcrowdDelta: +10,
  /** manual attention covers this many slots; automator adds level × coverage. */
  manualBaseline: 2,
  automatorCoverage: 1,
  undertreatPerIssue: +3,
  undertreatCap: +15,
  fullStaffDelta: -5,
  lowHeatDelta: -3,
  lowHeatThreshold: 20,
  /** Within a failed roll: minor vs major split. */
  minorShare: 0.6,
  deathChanceOnMajor: 0.05,
} as const;

/** The mailbox (v0.3 RFC §3): arrival cadence + tier-expansion economics. */
export const MAILBOX = {
  /** RULED: minimum ticks between letter arrivals (one per tick max). */
  minGapTicks: 10,
} as const;

/**
 * Meters & consequences (v0.5 "Consequences", ADR-009). Reputation + Heat as
 * live game-over meters, the Death-Letter claw-back economy, the broker, and
 * the mitigation staff. All PLACEHOLDER numbers (v0.8 owns balance); v0.5 ships
 * the MECHANISM.
 */
export const METERS = {
  /** Game-over thresholds → forced prestige (never a save wipe). */
  repFloor: -100, // reputation collapse
  heatCeiling: 100, // state shutdown
  /** Compounding: above this Heat, rep losses are amplified (death spiral). */
  hotThreshold: 70,
  repLossMultiplier: 1.5,
  /** Forced prestige pays LESS than a voluntary sale would have. */
  forcedPayoutMult: 0.5,
  /** Passive per-tick Heat decay from Compliance Officer level + nature. */
  naturalHeatDecay: 0.05,
  complianceHeatDecayPerLevel: 0.4,
  /** PR Firm: per-tick recovery of NEGATIVE reputation + incoming-loss cut. */
  prRepRecoverPerLevel: 0.1,
  prRepLossCutPerLevel: 0.12, // fraction of a rep loss absorbed per level (capped)
  prRepLossCutCap: 0.75,
  /** Mitigation-staff cost curve (shared upgradeCost idiom). */
  prFirmBaseCost: 400,
  complianceBaseCost: 400,
  mitigationCostGrowth: 2.0,
  mitigationMaxLevel: 5,
  /** Heat added per AGGRESSIVE-policy completion (short-stay churn tell). */
  aggressiveDischargeHeat: 1.5,
  /** Body broker: cash + heat per sold lead; broker-history relapse modifier. */
  brokerPayout: 120,
  brokerHeat: 6,
  /** v0.10 Control Room: authoritative tick lockout between broker sales
   * (1 tick = 1s online) — the #40 exploit patch, tuned in the instrument. */
  brokerCooldownTicks: 45,
  /** Relapse formula: +pts per prior broker sale (capped) — dirty-house tax. */
  brokerHistoryPerSale: 1.5,
  brokerHistoryCap: 20,
  /** Death Letter claw-back economy (per accumulated death). */
  respondCostPerDeath: 60,
  settleCostPerDeath: 150,
  // Flat rep/heat credit PER death. Capped at the base hit (ECONOMY.death* = 30
  // rep / 20 heat) so SETTLE fully undoes it but never NET-GAINS — v0.8 fix:
  // with the −50→−30 rep-hit retune, the old +50 SETTLE credit paid you to
  // profit from a death. SETTLE = full undo; RESPOND = genuinely partial.
  respondRepPerDeath: 15,
  respondHeatPerDeath: 8,
  settleRepPerDeath: 30,
  settleHeatPerDeath: 20,
  /** Quarterly Outcomes Report cadence (a "quarter" in ticks). */
  quarterlyPeriodTicks: 200,
  /** DEA meter (v0.6): activates with the criminal track; NO natural decay
   *  (v0.15 §8.4 — the threat recedes ONLY when you pay to reduce it). */
  deaCeiling: 100, // federal raid → forced prestige
  /** Raid payout is HARSHER than a state shutdown (voluntary > shutdown > raid). */
  raidPayoutMult: 0.25,
  /** v0.15 "The Fixer": the DEA-axis mitigation role (mirror of PR Firm /
   *  Compliance Officer). Leveled, funding-priced on the shared curve. */
  fixerBaseCost: 4000,
  /** DEA/tick the fixer WANTS to remove, per level. Finite by design — a big
   *  enough criminal build out-earns it (G4: the raid still bites). */
  fixerDeaMitigatePerLevel: 0.9,
  /** §8.2 UPKEEP SALARY: the per-tick bribe scales with the DEA mitigated —
   *  bribe/tick = fixerLevel · fixerDeaMitigatePerLevel · fixerBribePerDeaPoint,
   *  drawn from funding BEFORE mitigation. Underfund it → proportional safety. */
  fixerBribePerDeaPoint: 900,
} as const;

/**
 * Criminal track (v0.6 "The Other Business", ADR-015). Cost curve for run-level
 * criminal upgrades, the relapse cap the irony engine can't exceed, and the
 * Street-Knowledge prestige payout coefficients. PLACEHOLDERS (v0.8 balances).
 */
export const CRIMINAL = {
  costGrowth: 2.0, // per-level cost multiplier (shared upgradeCost idiom)
  /** v0.15 §8.3: the live master dial on ALL criminal income (Control-Room
   *  tunable). Base per-upgrade income in data/criminal.ts was already cut ÷4
   *  to match the ×4 pacing pass; this multiplier is the human-tuned knob on
   *  top (1.0 = ship the cut values; drop it to nerf the track further). */
  incomeScale: 1.0,
  /** The irony engine can't push criminal relapse past this many points. */
  relapseCap: 30,
  /** Street Knowledge earned on prestige = tierReached·perTier + peakCrimIncome·perIncome. */
  skPerTier: 8,
  skPerIncome: 1.5,
  /** MUST equal the deepest tier in data/criminal.ts (the save validator bounds
   *  tierAccess by the max upgrade tier; they agree only while this matches). */
  tierCount: 5,
} as const;

/**
 * Tier-expansion offers (v0.3 RFC §4, three-layer model): REQUIREMENTS gate
 * the offer letter's ARRIVAL (never time-based); PRICE is the purchase.
 * requireCompletions counts completions at the run's current frontier tier.
 */
export type ExpansionTier = "residential" | "php" | "iop" | "soberLiving" | "alumni";
export const EXPANSION: Record<
  ExpansionTier,
  { requireCompletions: number; requireFunding: number; price: number }
> = {
  residential: { requireCompletions: 3, requireFunding: 500, price: 500 },
  php: { requireCompletions: 3, requireFunding: 1500, price: 1500 },
  iop: { requireCompletions: 3, requireFunding: 4500, price: 4500 },
  soberLiving: { requireCompletions: 3, requireFunding: 13500, price: 13500 },
  alumni: { requireCompletions: 3, requireFunding: 0, price: 40000 },
};

/**
 * Prestige (v0.4 RFC §3–§5). Payout COEFFICIENTS + the offer gate + the
 * discount floors the shop's faster-climb perks can't push past. Numbers are
 * placeholders (v0.8 owns balance); the MECHANISM is what v0.4 ships.
 */
export const PRESTIGE = {
  /**
   * The "Sell the Facility" offer gate. MUST be reachable on a FRESH account,
   * or the whole prestige loop soft-locks: a new run starts at markAtRunStart
   * "none", so `expansionEligible` only ever offers Detox — a fresh account can
   * never reach a Residential frontier without prestiging first, and prestige
   * is what this gates. So the gate is Detox-frontier + some completions ("run
   * the floor first", not tick-2). Each prestige then advances the high-water
   * mark one rung, so the NEXT run can open one tier deeper — the ladder climbs
   * one tier per prestige. (Corrects the RFC §11.1 "≥ Residential" ruling, which
   * was unreachable as specified — see COMPLIANCE-v0.4 deviation.)
   */
  offerMinFrontier: "detox" as CareTierId,
  /** …and this many completions at that frontier — enough to have run the floor
   *  a meaningful while before the first cash-out (v0.8 pacing; iterated later). */
  offerMinCompletions: 20,
  /** f(run) = completionsThisRun·perGraduation + peakNet·perPeakNet + frontierBonus. */
  perGraduation: 5,
  perPeakNet: 2,
  /** Deepest-tier-reached-this-run bonus (frontier depth). */
  frontierBonus: { detox: 0, residential: 10, php: 25, iop: 50, soberLiving: 100 } as Record<CareTierId, number>,
  /** Multiplier floors — faster-climb perks can never zero a cost/requirement. */
  priceDiscountFloor: 0.4, // ≤60% off expansion price
  requirementDiscountFloor: 0.5, // ≤50% off required completions
} as const;

/** M12 tagline ladder (reputation thresholds, descending). */
export const TAGLINES: Array<{ minRep: number; text: string }> = [
  { minRep: 50, text: "A Fresh Start" },
  { minRep: 0, text: "Second Chances Happen" },
  { minRep: -50, text: "We Still Take Walk-Ins" },
  { minRep: -Infinity, text: "Now Under New Management" },
];

export const DEFAULT_FACILITY_NAME = "Serenity Halfway House";

/** M11 alumni engine. */
export const ALUMNI = {
  repPerAlumnusPerTick: 0.02,
  referralChancePerAlumnusPerTick: 0.002,
  referralChanceCapPerTick: 0.5,
  relapseChancePerAlumnusPerTick: 0.0005,
  contactCallBoostTicks: 30,
  contactCallMultiplier: 2,
  /** Outreach Coordinator: passive referral multiplier per level. */
  coordinatorMultiplierPerLevel: 0.25, // ×(1 + 0.25·level)
  coordinatorBaseCost: 5000,
  visibleSpriteCap: 20,
} as const;

export const ECONOMY = {
  /** Graduation (frontier completion) pays the tier payout × this. */
  graduationMultiplier: 1.5,
  // v0.8 pacing: graduations recover rep faster + a death costs less rep, so a
  // normal death rate doesn't collapse Reputation on deep (many-patient) runs.
  graduationReputation: 10,
  deathReputationHit: -30,
  deathHeatSpike: 20,
  startingFunding: 20,
  netWindowTicks: 60,
} as const;

/** Upgrade cost helper — the one curve idiom (priced at pre-upgrade level). */
export function upgradeCost(base: number, growth: number, currentLevel: number): number {
  return Math.round(base * growth ** currentLevel);
}
